import React, {useState, useRef, useEffect, useContext, memo} from 'react';

import { Button, Modal, 
			OverlayTrigger, Tooltip
		} from "react-bootstrap";
import ImageFadeIn from "react-image-fade-in";

import Select, { createFilter } from "react-select";
import createFilterOptions from "react-select-fast-filter-options";

import {vocab, pos} from './helpers/data';
import {UserContext} from "../../providers/UserProvider";
import {Box, getBorderColor, colorStyles} from "./helpers/blocks";

import MorphologicalInterface from './interfaces/MorphologicalInterface';

import BigText from './helpers/BigText';
import LoadingPage from './helpers/LoadingPage';

import cross from './helpers/cross.png';
import question from './helpers/questionOrange.png';

import './helpers/blocks.css';
import '../css/Components.css';

const LQV_Identification =memo(({curQ, curQ2, setCurQComplete, setCurQComplete2,
									curPi, setPartInst, setAnsSaved, btnClicked, allVerseQuestions}) =>{
	// To autosave when next or previous buttons are clicked
	useEffect(()=>{
		if (btnClicked===true){
			check('All', 0, 0);
			setAnsSaved(true);
		};
	}, [btnClicked]);

	const [loading, setLoading] =useState(false);

	const {currentUser} = useContext(UserContext);

	const [interfaceTitle, setInterfaceTitle] =useState(''); 
	const [interfaceIndex, setInterfaceIndex] =useState(0);
	const [interfaceQuestionIndex, setInterfaceQuestionIndex] =useState(0);
	const [showInterface, setShowInterface] =useState(false);
	
	//Enter = Submit
	const [btn, setBtn]=useState(null)

	const interfaceOpen =useRef(null);
	useEffect(()=>{
		interfaceOpen.current =showInterface;
	}, [showInterface]);
	
	const handleKeyDown=(event)=>{
		// console.log('key=', event.key)
		if(event.key==='Enter' & !interfaceOpen.current){
			event.preventDefault();
			btn.click();
		}
	};
	useEffect(() => {
		if (btn){
			// create an eventListener
			window.addEventListener('keydown', e=>handleKeyDown(e));
		}
	}, [btn]);
	useEffect(()=>{
		return ()=>{
			// remove eventlistener on unmount
			window.removeEventListener('keydown', e=>handleKeyDown(e))
		}
	}, [])

	const filterOptions = createFilterOptions({vocab});

	const getPlaceholder =(selected, temp, options=[])=>{
		// console.log(selected, temp, options);
		if (selected.length>0)
			return selected
		else if (options.length>1)
			return options[0].value+'?';
		else
			return temp;
	};

	const [questions, setQuestions] = useState([]);
	const [qTexts, setQTexts] = useState([]);
	const [ansStates, setAnsStates] = useState([]);
	const [showText, setShowText] = useState('');

	// Track refs for inflection inputs so we can restore focus after re-renders
	const inputRefs = useRef({});

	useEffect(() => {
		// Load questions only from allVerseQuestions when it is received.
		// This should not change due to user input; only prop changes (allVerseQuestions)
		// or backend fetches elsewhere should update questions/qTexts/ansStates.
		if (allVerseQuestions && allVerseQuestions.length > 0) {
			setQuestions(allVerseQuestions);
			setQTexts(allVerseQuestions.map(q => q.text || ''));
			setAnsStates(allVerseQuestions.map(q => q.uans || []));
		}
	}, [allVerseQuestions]);

	useEffect(() => {
		setShowText(qTexts.join("<br>"));
	}, [qTexts]);
	
	const handleInputChange = (questionIndex, index, event, word) => {
		const inputKey = `${questionIndex}-${index}`;
		const newAnsStates = [...ansStates];
		const values = [...newAnsStates[questionIndex]];
		switch(event.target.name){
			case "inflection":
				values[index].inflection = event.target.value;
				if (values[index].inflection==='?' || values[index].inflection==='/'){
					setInterfaceTitle(word);
					setInterfaceIndex(index);
					setInterfaceQuestionIndex(questionIndex);
					setShowInterface(true);
				}
				break;
			default:
				break;
		}
		newAnsStates[questionIndex] = values;
		setAnsStates(newAnsStates);

		// Explicitly restore focus to the same input after React re-renders
		if (typeof document !== "undefined") {
			setTimeout(() => {
				const el = inputRefs.current[inputKey];
				if (el) {
					const len = el.value.length;
					el.focus();
					try {
						el.setSelectionRange(len, len);
					} catch (e) {
						// Some input types don't support selectionRange; ignore
					}
				}
			}, 0);
		}
	};

	const handleSelectChange = (questionIndex, index, name, selected) => {
		const newAnsStates = [...ansStates];
		const values = [...newAnsStates[questionIndex]];

		// console.log('selected:', selected);
		if (selected===null){
			return;
		}
	
		var options =[];
		switch(name){
			case "lemma":
				values[index].lemma = selected.value;
				// console.log("lemma", values[index].lemma);

				options =[];
				pos[selected.value].forEach(tag =>{
					options.push({ label:tag, value: tag })
				});
				values[index].posOptions =options
				if (options.length===1)
					values[index].pos =options[0].value;
				else
					values[index].pos =''
				// console.log("posOptions", values[index].posOptions);

				if(!selected.isLemma) {
					//Add rootOptions
					values[index].addRoot=true;
					var rootOptions = [];
					selected.parents.forEach(root =>{
						rootOptions.push({ label:root, value: root })
					});
					values[index].rootOptions =rootOptions;
					if (rootOptions.length===1)
						values[index].root =rootOptions[0].value;
					else
						values[index].root=''

					// Add rootPosOptions
					var rootPosOptions =[]; //for rootPos
					pos[values[index].root].forEach(tag =>{
						rootPosOptions.push({ label:tag, value: tag })
					});
					values[index].rootPosOptions = rootPosOptions;
					if (rootPosOptions.length===1)
						values[index].rootPos =rootPosOptions[0].value;
					else
						values[index].rootPos =''
				}
				else{
					//Remove rootOptions
					values[index].addRoot=false;
					values[index].root ='';
					values[index].rootPos='';
					values[index].rootOptions =[];
					values[index].rootPosOptions =[];
				}
				break;

			case "pos":
				values[index].pos =selected.value;
				break;

			case "root":
				values[index].root = selected.value;
				// console.log("root", values[index].root);

				options =[]; //for rootPos
				pos[selected.value].forEach(tag =>{
					options.push({ label:tag, value: tag })
				});
				values[index].rootPosOptions = options
				if (options.length===1)
						values[index].rootPos =options[0].value;
				else
					values[index].rootPos =''
				// console.log("rootPosOptions", values[index].rootPosOptions);
				break

			case "rootPos":
				values[index].rootPos =selected.value;
				break;
			
			default:
				break;
		}
		
		newAnsStates[questionIndex] = values;
		setAnsStates(newAnsStates);
	};

	// Checking the user's answers
	//checking "Box" or "All" depending on checkType
	const check =(checkType, questionIndex, wordIndex=0) =>{	
		console.log("checking:"+checkType +" | questionIndex="+questionIndex +" | wordIndex="+wordIndex);
	
		const postObj = {
			method: 'POST',
			cache: 'no-cache',
			headers: {'Content-Type': 'application/json'},
			body: JSON.stringify({ uans: ansStates, curPart: 'identification',
											pi: curPi, email: currentUser.email, checkType: checkType, 
											questionIndex: questionIndex, wordIndex: wordIndex,
											curQs: questions })
		}
	
		const getFeedback =async () => {
			setLoading(true);

			const response = await fetch(process.env.REACT_APP_FLASK_API+'check_lqv_uans', postObj);
			const data = await response.json();
			console.log("##G O T data:", data);

			// Backend now returns single analysis object with afterState[0] = array of qTexts, afterState[1] = array of ansStates
			if (data.analysis && data.analysis.afterState) {
				const allQTexts = data.analysis.afterState[0];  // Array of qTexts
				const allAnsStates = data.analysis.afterState[1];  // Array of ansStates
				
				if (checkType==='All') {
					// Update all questions
					setQTexts(allQTexts);
					setAnsStates(allAnsStates);
				}
				else if (checkType ==="Box") {
					// Update specific question, preserve others
					const newQTexts = [...qTexts];
					const newAnsStates = [...ansStates];
					if (allQTexts[questionIndex] !== null && allQTexts[questionIndex] !== undefined) {
						newQTexts[questionIndex] = allQTexts[questionIndex];
					}
					if (allAnsStates[questionIndex] !== null && allAnsStates[questionIndex] !== undefined) {
						newAnsStates[questionIndex] = allAnsStates[questionIndex];
					}
					setQTexts(newQTexts);
					setAnsStates(newAnsStates);
				}
			}

			setPartInst(data.instruction);
			setCurQComplete(data.partComplete);

			setLoading(false);
		};
		getFeedback();
	};

	const [showPdf, setShowPdf] =useState(false);
	const [pdfFile, setPDFfile]=useState('agni.pdf');
	const InflectionError =(word, message, pdf, blank=false) => {
		if (message.length===0)
			message ="Your inflectional identifier is incorrect."

		if (blank)
			return <OverlayTrigger
						placement="top"
						overlay={<Tooltip>Your inflectional identifier is blank.</Tooltip>}
					>
						<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
							width={25} height={25} src={question} alt='empty'/>
					</OverlayTrigger>
		else if (pdf.length===0) {
			return <OverlayTrigger
						placement="top"
						overlay={<Tooltip>{message}</Tooltip>}
					>
						<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
							width={25} height={25} src={cross} alt='wrong'/>
					</OverlayTrigger>
		}
		else {
			return (
				<>
					<Modal 
							show={showPdf} 
							onHide={()=>setShowPdf(false)} 
							dialogClassName="pdfmodal-90w"
					>
						<Modal.Header closeButton> 
							<div style={{display:'flex', justifyContent:'center', width:'100%'}}>
								<span style={{fontSize:'24pt', fontFamily:'Shobhika'}}>{word}</span>
							</div>
						</Modal.Header>
						<embed
							title="helpPdf"
							src={require("../../docs/"+pdfFile)+"#zoom=150"}
							frameBorder="0"
							scrolling="auto"
							height="100%"
							width="100%"
						></embed>
					</Modal>

					<OverlayTrigger
						placement="top"
						overlay={<Tooltip>{message}</Tooltip>}
					>
						<ImageFadeIn opacityTransition={3} 
							style={{float:'right', cursor:'pointer'}} width={25} height={25}
							// setShowPdf to true
							onClick={()=>{setPDFfile(pdf);setShowPdf(true)}} src={cross} alt='wrong'/>
					</OverlayTrigger>
				</>
			);
		}
	}
	
	const OneState =({cur, index, curQ, questionIndex}) => (
		<Box key={`${cur.title}${index}`} borderColor={getBorderColor(cur.errorFound, cur.allCorrect)}>
			<div className='boxTitle'>
				{curQ.parts[curPi]==='identification' &&
					<div style={{fontSize:'28px', fontFamily:'Shobhika'}}>
						{cur.title}
					</div>
				}
				{curQ.parts[curPi]==='verb-identification' &&
					<>{cur.title}</>
				}
				<div className="line" />
			</div>

			<div className="form-group">
				<label htmlFor={`"inflection"${index}`} className="smText">Inflection
					{cur.inflectionError===true&&
						InflectionError(cur.title, cur.help.message, cur.help.pdf)
					}
					{cur.inflectionError==='?'&&
						InflectionError(cur.title, cur.help.message, cur.help.pdf, true)
					}
				</label>
				<input
					type="text"
					autoComplete="off"
					className="form-control"

					name="inflection"
					id={`"inflection"${index}`}
					aria-describedby={`"inflectionError"${index}`}
					
					placeholder="Third"
					value={cur.inflection}
					onChange={(event) => handleInputChange(questionIndex, index, event, cur.title)}
					ref={(el) => {
						if (el) {
							inputRefs.current[`${questionIndex}-${index}`] = el;
						}
					}}
				/>
			</div>

			<div className="form-group">
				<label htmlFor={`"lemma"${index}`} className="smText">Lemma
				{cur.lemmaError===true&&
					<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
					width={25} height={25} src={cross} alt='wrong'/>
				}
				{cur.lemmaError==='?'&&
					<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
					width={25} height={25} src={question} alt='empty'/>
				}
				</label>
				<Select
					backspaceRemovesValue
					// autoFocus={index===0}
					isClearable
					
					name="lemma"
					id={`"lemma"${index}`}
					aria-describedby={`"lemmaError"${index}`}
					
					options={vocab}
					styles={colorStyles}
					maxMenuHeight={200}

					filterOptions={filterOptions}
					filterOption={createFilter({ignoreCase:false, ignoreAccents:false, trim:true,  matchFrom:'start'})}
					
					placeholder={getPlaceholder(cur.lemma, "First")}
					onChange={(selectedOption) => handleSelectChange(questionIndex, index, "lemma", selectedOption)}

					// menuPortalTarget={document.body}
				/>
				
			</div>

			<div className="form-group">
				<label htmlFor={`"pos"${index}`} className="smText">Lexical category
				{cur.posError===true&&
					<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
					width={25} height={25} src={cross} alt='wrong'/>
				}
				{cur.posError==='?'&&
					<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
					width={25} height={25} src={question} alt='empty'/>
				}
				</label>
				<Select
					backspaceRemovesValue
					name="pos"
					id={`"pos"${index}`}
					aria-describedby={`"posError"${index}`}

					options={cur.posOptions}

					placeholder={getPlaceholder(cur.pos, "Second", cur.posOptions)}
					// selectValue ={cur.pos}
					onChange={(selectedOption) => handleSelectChange(questionIndex, index, "pos", selectedOption)}

					// menuPortalTarget={document.querySelector("body")}
				/>	
			</div>

			{cur.addRoot && 
				<>
					<div className="form-group">
						<label htmlFor={`"root"${index}`} className="smText">Root
						{cur.rootError===true&&
							<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
							width={25} height={25} src={cross} alt='wrong'/>
						}
						{cur.rootError==='?'&&
							<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
							width={25} height={25} src={question} alt='empty'/>
						}
						</label>
						<Select
							backspaceRemovesValue
							name="root"
							id={`"root"${index}`}
							aria-describedby={`"rootError"${index}`}

							options={cur.rootOptions}

							placeholder={getPlaceholder(cur.root, "Fourth", cur.rootOptions)}
							// selectValue ={cur.root}
							onChange={(selectedOption) => handleSelectChange(questionIndex, index, "root", selectedOption)}

							// menuPortalTarget={document.querySelector("body")}
						/>
					</div>
					<div className="form-group">
						<label htmlFor={`"root"${index}`} className="smText">
							Root's lexical category
							{cur.rootPosError===true&&
								<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
								width={25} height={25} src={cross} alt='wrong'/>
							}
							{cur.rootPosError==='?'&&
								<ImageFadeIn opacityTransition={3} style={{float:'right'}} 
								width={25} height={25} src={question} alt='empty'/>
							}
						</label>
						<Select
							backspaceRemovesValue
							name="rootPos"
							id={`"rootPos"${index}`}
							aria-describedby={`"rootPosError"${index}`}

							options={cur.rootPosOptions}

							placeholder={getPlaceholder(cur.rootPos, "Fifth", cur.rootPosOptions)}
							// selectValue ={cur.rootPos}
							onChange={(selectedOption) => handleSelectChange(questionIndex, index, "rootPos", selectedOption)}

							// menuPortalTarget={document.querySelector("body")}
						/>
					</div>
				</>
			}

			<Button variant='outline-primary' size='sm' style={{'float':'right', 'marginTop':'5px'}}
				onClick={()=>check('Box', questionIndex, index)}>Check
			</Button>

		</Box>
	);

	return (
		<div>
			<LoadingPage loading={loading} />
			
			{questions.length > 0 && questions[0].parts[curPi]==='identification' &&
				<MorphologicalInterface
					show={showInterface}
					onHide={()=>setShowInterface(false)}
					title={interfaceTitle}
					index={interfaceIndex}
					ansstate={ansStates[interfaceQuestionIndex] || []}
					setans={(newAns)=>{
						const newAnsStates = [...ansStates];
						newAnsStates[interfaceQuestionIndex] = newAns;
						setAnsStates(newAnsStates);
					}}
				/>
			}

			{questions.length > 0 && questions[0].takeInput &&
			<>
				<span style={{fontFamily:'Shobhika'}}>
					<BigText sp="Q:" text={showText} colored={true}/> 
				</span>
				
				<div className="FlexBox">
					<h4><span className="blueText">A:</span></h4>
					<div className="containerBox">
						{questions.map((q, questionIndex) => (
							q.takeInput && ansStates[questionIndex] && ansStates[questionIndex].map((cur, wordIndex) => (
								<OneState 
									key={`${questionIndex}-${wordIndex}`}
									cur={cur} 
									index={wordIndex} 
									curQ={q} 
									questionIndex={questionIndex}
								/>
							))
						))}
					</div>
				</div>
	 
				<div className="KeepRight">
					<Button variant='primary' 
						onClick={()=>check('All', 0, 0)} 
						ref={node=>setBtn(node)}
					>
						Check all
					</Button>
				</div>			
			</>
			}
		</div>
	);
});

export default LQV_Identification;

// Archives
/////////////////////////

// {ansState.map((cur, index)=>(
// 	<>{OneState({cur: cur, index: index, curQ: curQ, lineNo: 1})}</>
// ))}
// {ansState2.map((cur, index)=>(
// 	<>{OneState({cur: cur, index: index, curQ: curQ2, lineNo: 2})}</>
// ))}


///////////////////

/* before {cur.addRoot} 
		{!cur.addRoot &&
			<div style={{display:'flex'}}>
				<OverlayTrigger
					key='top'
					placement='top'
					overlay={
						<Tooltip id={`tooltipAdd${index}`}>
							Adds Root Fields
						</Tooltip>
					}
				>
					<Button variant='primary' size='sm' style={{'display':'block'}}
						onClick={e=>addRootFields(index)}>+
					</Button>
				</OverlayTrigger>
				{cur.addRootError&&
					<div className="redText" style={{marginLeft:'10px'}}>
						Please Add Root Fields.
					</div>
				}
			</div>
		} 
*/

/* Inside {cur.addRoot}
		<div style={{display:'flex'}}>
			<OverlayTrigger
				key='top'
				placement='top'
				overlay={
					<Tooltip id={`tooltipRemove${index}`}>
						Removes Root Fields
					</Tooltip>
				}
			>
				<Button variant='danger' size='sm' 
					onClick={e=>removeRootFields(index)}> - 
				</Button>
			</OverlayTrigger>
			{cur.removeRootError&&
				<div className="redText" style={{marginLeft:'10px'}}>
					Please Remove Root Fields.
				</div>
			}
		</div> 
*/